// src/feature-module/settings/administration/Users/View Users/UserTypeModal.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Table, Button, Spinner } from "react-bootstrap";
import apiDefinitions from "../../../../../core/api/apiDefinitions";

interface User {
  empNumber: string;
  name: string;
  createdAt: string;
  status: string;
  email?: string;
  phone?: string;
  userType?: string;
  departmentNames?: string[];
  licenseNumber?: string;
  licenseType?: string;
  expireDate?: string;
}

interface UserTypeModalProps {
  show: boolean;
  onHide: () => void;
  selectedUserType: string;
  userTypeUsers: User[];
  loading: boolean;
  onView: (user: User) => void;
}

const UserTypeModal: React.FC<UserTypeModalProps> = ({
  show,
  onHide,
  selectedUserType,
  userTypeUsers,
  loading,
  onView,
}) => {
  const [hydratedUsers, setHydratedUsers] = useState<User[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const hasRows = useMemo(() => userTypeUsers && userTypeUsers.length > 0, [userTypeUsers]);

  const formatDate = (iso?: string | null) => {
    if (!iso) return "";
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  useEffect(() => {
    let cancelled = false;

    const hydrate = async () => {
      if (!show || !hasRows) {
        setHydratedUsers(userTypeUsers);
        return;
      }

      setLoadingDetails(true);
      try {
        const results = await Promise.all(
          userTypeUsers.map(async (u) => {
            try {
              const res = await apiDefinitions.getUserByEmpNumber(u.empNumber);
              if (res?.data?.status === "S1000" && res?.data?.data) {
                const d = res.data.data as {
                  createdAt?: string;
                  status?: string;
                  name?: string;
                };
                return {
                  ...u,
                  // prefer freshest values from the detail API:
                  name: d.name ?? u.name,
                  createdAt: d.createdAt ? formatDate(d.createdAt) : u.createdAt,
                  status: d.status ?? u.status,
                };
              }
            } catch {
              // swallow and keep original row if detail fetch fails
            }
            return u;
          })
        );

        if (!cancelled) setHydratedUsers(results);
      } finally {
        if (!cancelled) setLoadingDetails(false);
      }
    };

    hydrate();

    return () => {
      cancelled = true;
    };
  }, [show, hasRows, userTypeUsers]);

  const isBusy = loading || loadingDetails;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{selectedUserType || "Users"} — List</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isBusy && (
          <div className="d-flex align-items-center gap-2 mb-2">
            <Spinner animation="border" size="sm" />
            <span>Loading{loadingDetails ? " details" : ""}…</span>
          </div>
        )}

        <div className="table-responsive">
          <Table className="table text-nowrap table-borderless">
            <thead>
              <tr>
                <th scope="col">User Name</th>
                <th scope="col">Employee Id</th>
                <th scope="col">Created</th>
                <th scope="col">Status</th>
                <th scope="col">Action</th>
              </tr>
            </thead>
            <tbody>
              {isBusy && hydratedUsers.length === 0 ? (
                <tr>
                  <td colSpan={5}>Loading…</td>
                </tr>
              ) : hydratedUsers.length === 0 ? (
                <tr>
                  <td colSpan={5}>No users found for {selectedUserType}</td>
                </tr>
              ) : (
                hydratedUsers.map((user) => (
                  <tr key={user.empNumber}>
                    <th scope="row">
                        {(user.name || "").trim().split(/\s+/).slice(-3).join(" ")}
                        </th>
                    <td>{user.empNumber}</td>
                    <td>{user.createdAt}</td>
                    <td>
                      <span
                        className={`badge bg-${
                          user.status === "INITIAL"
                            ? "secondary"
                            : user.status === "PASSWORD_RESET"
                            ? "primary"
                            : user.status === "ACTIVE"
                            ? "success"
                            : user.status === "active"
                            ? "success"
                            : "info"
                        }`}
                      >
                        {user.status}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-icon btn-sm btn-soft-secondary rounded-pill"
                        onClick={() => onView(user)}
                        title="View User"
                        disabled={isBusy}
                      >
                        <i className="feather-eye" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="primary" onClick={onHide} disabled={isBusy}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UserTypeModal;
